/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import tinycolor from 'tinycolor2';
import { Global, css } from '@emotion/core';

import ViewOnlyAnalysis from './components/presentational/ViewOnlyAnalysis';
import ViewOnlyOverview from './components/presentational/ViewOnlyOverview';
import InvalidTokenModal from './components/presentational/InvalidTokenModal';
import SCLegendInfoModal from './components/presentational/SCLegendInfoModal';
import SharedTemplateAboutModal from '../sports-science/components/presentational/sharedTemplateComponents/SharedTemplateAboutModal';
import Button from '../../shared/_TBComponents/Button';
import HeaderText from '../../shared/_TBComponents/HeaderText';
import Spinner from '../../shared/_TBComponents/Spinner';
import DarkLogo from '../../shared/images/poweredByTBBlack.png';
import LightLogo from '../../shared/images/poweredByTBWhite.png';
import {
  fetchPublicSCDashData,
  setAboutDashboardModalShowing,
  setSCDashView,
} from './ducks/sportCoachDashboardActions';

const Container = styled('div')`
  width: 100%;
  overflow-x: auto !important;
`;

const DashboardBanner = styled('div')`
  align-items: center;
  background-color: ${(props) => props.bgColor};
  color: #ffffff;
  display: flex;
  height: 100px;
  justify-content: space-between;
  padding: 10px 15px;
`;

const DashboardHeader = styled('div')`
  align-items: center;
  background-color: #ffffff;
  display: flex;
  justify-content: space-between;
  padding-bottom: 10px;
  padding-left: 20px;
  padding-top: 30px;
  width: 100%;
`;

const MenuContainer = styled('div')`
  display: flex;
`;

const MenuItem = styled('button')`
  border: none;
  border-bottom: ${(props) => (props.isActive ? '3px solid #1787FB!important' : '3px solid white')};
  color: ${(props) => (!props.isActive ? '#9E9E9E' : '#444444')};
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  margin-left: 10px;
  margin-right: 12px;
`;

const PageWrapper = styled('div')`
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
`;

const TBuildrLogo = styled('div')`
  background-image: ${(props) => (tinycolor(props.bgColor).isLight()
    ? `url(${DarkLogo})`
    : `url(${LightLogo})`)};
  background-repeat: no-repeat;
  height: 46px;
  width: 200px;
`;

const TitleAndInfo = styled('div')`
  display: flex;

  button {
    margin-left: 5px;
  }

  svg {
    border: 1px solid #808080;
    border-radius: 50%;
    height: 20px;
    padding: 3px;
    width: 20px;
  }
`;

const UserLogo = styled('img')`
  height: 75px;
`;

const Wrapper = styled('div')`
  height: 100%;
`;

const ViewOnlySCDash = () => {
  const {
    dashBGColor,
    dashLogo,
    dashTitle,
    scDashData,
    scDashError,
  } = useSelector((state) => state.sportCoachDashboard.data);
  const {
    aboutDashboardModalShowing,
    isLegendInfoModalShowing,
    isPublicSCDashDataLoading,
    scDashView,
  } = useSelector((state) => state.sportCoachDashboard.ui);

  const dispatch = useDispatch();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token');

  const noData = !scDashError
    && !scDashData?.chartData?.length
    && !scDashData?.cardData?.length;
  const hasData = !scDashError
    && (!!scDashData?.chartData?.length || !!scDashData?.cardData?.length);

  // Fetch data on page load with token if there's no data
  useEffect(() => {
    dispatch(fetchPublicSCDashData(token));
  }, [dispatch, token]);

  const handleInfoClick = () => {
    dispatch(setAboutDashboardModalShowing(true));
  };

  const handleMenuClick = (view) => {
    if (view !== scDashView) {
      dispatch(setSCDashView(view));
    }
  };

  const renderContent = () => {
    if (isPublicSCDashDataLoading) {
      return (
        <Wrapper>
          <Spinner large />
        </Wrapper>
      );
    }

    return (
      <>
        {(scDashError || (noData && !isPublicSCDashDataLoading)) && (
          <InvalidTokenModal noData={noData} />
        )}
        <PageWrapper>
          {isLegendInfoModalShowing && <SCLegendInfoModal />}
          <DashboardBanner bgColor={dashBGColor || '#444444'}>
            {dashLogo ? (
              <UserLogo src={dashLogo} alt='user logo' id='userLogo' />
            ) : (
              <div />
            )}
            <TBuildrLogo bgColor={dashBGColor || '#444444'} />
          </DashboardBanner>
          {hasData && (
            <>
              <DashboardHeader>
                <TitleAndInfo>
                  <HeaderText fontWeight='300' letterSpacing='normal'>
                    {dashTitle}
                  </HeaderText>
                  <Button
                    aria-label='Show dashboard information'
                    bgColor='#00000000'
                    icon='info'
                    iconColor='#979797'
                    iconOnly
                    noBorder
                    onClick={handleInfoClick}
                    rounded
                  />
                </TitleAndInfo>
                <MenuContainer>
                  <MenuItem
                    isActive={scDashView === 'performance'}
                    onClick={() => handleMenuClick('performance')}
                  >
                    Performance Overview
                  </MenuItem>
                  <MenuItem
                    isActive={scDashView === 'group'}
                    onClick={() => handleMenuClick('group')}
                  >
                    Group Analysis
                  </MenuItem>
                </MenuContainer>
              </DashboardHeader>
              {scDashView === 'performance' && <ViewOnlyOverview />}
              {scDashView === 'group' && <ViewOnlyAnalysis />}
              {aboutDashboardModalShowing && (
                <SharedTemplateAboutModal dashboard='KPI Tracker' />
              )}
            </>
          )}
        </PageWrapper>
      </>
    );
  };

  return (
    <Container>
      <Global
        styles={css`
          body {
            overflow-y: hidden;
          }
        `}
      />
      <Helmet>
        <title>KPI Tracker | TeamBuildr</title>
      </Helmet>
      {renderContent()}
    </Container>
  );
};

export default ViewOnlySCDash;
